import { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  AppBar,
  Toolbar,
  CircularProgress,
  Pagination,
  Stack,
  TextField,
  IconButton,
  InputLabel,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import Colors from "../../assets/styles";
import AuthServices from "../../apis/AuthServices";
import SimpleDialog from "../../components/Modal/index";
import { useForm } from "react-hook-form";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { ErrorToaster, SuccessToaster } from "../../components/toaster";

export default function StationList() {
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(30);
  const [isLoading, setIsLoading] = useState(false);
  const [stations, setStations] = useState([]);
  const [filteredStations, setFilteredStations] = useState([]);
  const [selectedStation, setSelectedStation] = useState(null);
  const [editDialog, setEditDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isActive, setIsActive] = useState(true);

  const {
    handleSubmit,
    formState: { errors },
    setValue,
    register,
  } = useForm();

  const handlePageChange = (event, newPage) => {
    setPage(newPage - 1);
  };

  const getStations = async () => {
    setIsLoading(true);
    try {
      const result = await AuthServices.getStations();
      if (result.IsSuccess == true) {
        setStations(result.Response || []);
        setFilteredStations(result.Response || []);
      }
    } catch (error) {
      ErrorToaster("Something went Wrong");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getStations();
  }, []);

  useEffect(() => {
    if (!editDialog || !selectedStation) return;
    setValue("stationName", selectedStation?.StationName || "");
    setValue("stationNameUr", selectedStation?.StationNameUR || "");
    setValue("city", selectedStation?.City || "");
    setValue("latitude", selectedStation?.Latitude ?? "");
    setValue("longitude", selectedStation?.Longitude ?? "");
    setIsActive(selectedStation?.IsActive !== false);
  }, [editDialog, selectedStation, setValue]);

  const onSubmit = async (formData) => {
    const obj = {
      StationDetailsId: selectedStation?.StationDetailsId,
      StationName: formData?.stationName,
      StationNameUR: formData?.stationNameUr,
      City: formData?.city,
      Latitude: formData?.latitude ? Number(formData.latitude) : null,
      Longitude: formData?.longitude ? Number(formData.longitude) : null,
      IsActive: isActive,
    };
    try {
      const result = await AuthServices.updateStation(obj);
      if (result.IsSuccess == true) {
        SuccessToaster("Updated Successfully");
        getStations();
        setEditDialog(false);
      }
    } catch (error) {
      ErrorToaster("Something went Wrong");
    }
  };

  const deleteStation = async () => {
    try {
      const result = await AuthServices.deleteStation(
        selectedStation?.StationDetailsId
      );
      if (result.IsSuccess == true) {
        SuccessToaster("Deleted Successfully");
        getStations();
        setDeleteDialog(false);
      }
    } catch (error) {
      ErrorToaster("Something went Wrong");
    }
  };

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);
    const filtered = stations.filter(
      (station) =>
        station.StationName?.toLowerCase().includes(value) ||
        station.StationNameUR?.toLowerCase().includes(value) ||
        station.City?.toLowerCase().includes(value)
    );
    setFilteredStations(filtered);
    setPage(0);
  };

  return (
    <>
      <Box sx={{ mb: 3 }}>
        <AppBar position="static" color="transparent" elevation={0}>
          <Toolbar
            sx={{ justifyContent: "space-between", alignItems: "center" }}
          >
            <Box>
              <Typography
                variant="h5"
                component="h1"
                sx={{
                  color: Colors.primary,
                  fontWeight: 500,
                }}
              >
                Station List
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Station / Station List
              </Typography>
            </Box>
          </Toolbar>
        </AppBar>
      </Box>

      <SimpleDialog
        open={editDialog}
        onClose={() => setEditDialog(false)}
        maxWidth={"md"}
        title="Edit Station"
      >
        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexDirection: { xs: "column", md: "row" },
              mb: 3,
            }}
          >
            <Box sx={{ width: "100%" }}>
              <Typography variant="body2" sx={{ mb: 1, color: "#404e67" }}>
                Station Name
              </Typography>
              <TextField
                fullWidth
                {...register("stationName", {
                  required: "Station Name is Required",
                })}
                error={!!errors.stationName}
                helperText={errors.stationName ? errors.stationName.message : ""}
              />
            </Box>
            <Box sx={{ width: "100%" }}>
              <Typography variant="body2" sx={{ mb: 1, color: "#404e67" }}>
                Station Name (Urdu)
              </Typography>
              <TextField fullWidth {...register("stationNameUr")} />
            </Box>
            <Box sx={{ width: "100%" }}>
              <Typography variant="body2" sx={{ mb: 1, color: "#404e67" }}>
                City
              </Typography>
              <TextField
                fullWidth
                {...register("city", { required: "City is Required" })}
                error={!!errors.city}
                helperText={errors.city ? errors.city.message : ""}
              />
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexDirection: { xs: "column", md: "row" },
              mb: 3,
            }}
          >
            <Box sx={{ width: "100%" }}>
              <Typography variant="body2" sx={{ mb: 1, color: "#404e67" }}>
                Latitude
              </Typography>
              <TextField
                fullWidth
                type="number"
                inputProps={{ step: "any" }}
                {...register("latitude")}
              />
            </Box>
            <Box sx={{ width: "100%" }}>
              <Typography variant="body2" sx={{ mb: 1, color: "#404e67" }}>
                Longitude
              </Typography>
              <TextField
                fullWidth
                type="number"
                inputProps={{ step: "any" }}
                {...register("longitude")}
              />
            </Box>
            <Box sx={{ width: "100%", display: "flex", alignItems: "center" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isActive}
                    onChange={(event) => setIsActive(event.target.checked)}
                  />
                }
                label="Is Active"
              />
            </Box>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              type="submit"
              variant="contained"
              sx={{
                bgcolor: "darkgreen",
                "&:hover": {
                  bgcolor: "green",
                },
              }}
            >
              Update
            </Button>
          </Box>
        </Box>
      </SimpleDialog>

      <SimpleDialog
        open={deleteDialog}
        onClose={() => setDeleteDialog(false)}
      >
        <Typography variant="h6" sx={{ mb: 2, textAlign: "center" }}>
          Are you sure you want to Delete this Station?
        </Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-around",
            gap: 2,
            mt: 3,
          }}
        >
          <Button
            sx={{ background: "grey", color: "white", px: 2.5 }}
            onClick={() => setDeleteDialog(false)}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{ background: "rgb(0, 100, 0)" }}
            onClick={deleteStation}
          >
            Confirm
          </Button>
        </Box>
      </SimpleDialog>

      <Container
        maxWidth="xl"
        sx={{
          mt: 2,
          boxShadow: "0px 8px 24px 0 #4545501A",
          py: 2,
          backgroundColor: Colors.white,
          borderRadius: "12px",
        }}
      >
        <Box sx={{ mb: 2 }}>
          <InputLabel>Search</InputLabel>
          <TextField
            sx={{ width: { md: "50%", sm: "100%", xs: "100%" } }}
            placeholder="Search by Station Name or City"
            value={searchTerm}
            onChange={handleSearch}
          />
        </Box>
        <TableContainer sx={{ height: "auto", overflowX: "auto" }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "rgb(19 62 2)" }}>
                <TableCell sx={{ color: "white" }}>#</TableCell>
                <TableCell sx={{ color: "white" }}>Station Name</TableCell>
                <TableCell sx={{ color: "white" }}>Station Name (Urdu)</TableCell>
                <TableCell sx={{ color: "white" }}>City</TableCell>
                <TableCell sx={{ color: "white" }}>Latitude</TableCell>
                <TableCell sx={{ color: "white" }}>Longitude</TableCell>
                <TableCell sx={{ color: "white" }}>Active</TableCell>
                <TableCell sx={{ color: "white" }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : (
                filteredStations
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((station, index) => (
                    <TableRow key={station.StationDetailsId}>
                      <TableCell>{index + 1 + page * rowsPerPage}</TableCell>
                      <TableCell>{station.StationName}</TableCell>
                      <TableCell>{station.StationNameUR}</TableCell>
                      <TableCell>{station.City}</TableCell>
                      <TableCell>{station.Latitude ?? "-"}</TableCell>
                      <TableCell>{station.Longitude ?? "-"}</TableCell>
                      <TableCell>{station.IsActive ? "Yes" : "No"}</TableCell>
                      <TableCell>
                        <IconButton
                          aria-label="edit"
                          onClick={() => {
                            setSelectedStation(station);
                            setEditDialog(true);
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          aria-label="delete"
                          onClick={() => {
                            setSelectedStation(station);
                            setDeleteDialog(true);
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Stack
          direction="row"
          justifyContent="center"
          alignItems="center"
          sx={{ mt: 2 }}
        >
          <Pagination
            count={Math.ceil(filteredStations.length / rowsPerPage) || 1}
            page={page + 1}
            onChange={handlePageChange}
            variant="outlined"
            shape="rounded"
            color="success"
            sx={{
              "& .MuiPaginationItem-root": {
                borderColor: "rgb(19, 62, 2)",
                color: "rgb(19, 62, 2)",
              },
              "& .MuiPaginationItem-page.Mui-selected": {
                bgcolor: "rgb(19, 62, 2)",
                color: "white",
              },
            }}
          />
        </Stack>
      </Container>
    </>
  );
}
